import './settings-leaf.css';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reengagementApi, type ReengagementSettings } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, Checkbox, LoadingBlock } from '../ui.tsx';
import { NumberInput } from '../number-input.tsx';

type EditableSettings = Omit<ReengagementSettings, 'notificationsAvailable'>;

function editable(settings: ReengagementSettings): EditableSettings {
  const { notificationsAvailable: _computed, ...draft } = settings;
  return draft;
}

export default function ReengagementCard() {
  const { t } = useTranslation();
  const { user, can } = useSession();
  const queryClient = useQueryClient();
  const authorized = user?.role === 'owner' && can('company:write');
  const [draft, setDraft] = useState<EditableSettings | null>(null);
  const [saved, setSaved] = useState(false);

  const query = useQuery({
    queryKey: ['reengagement', 'settings'],
    queryFn: () => reengagementApi.settings(),
    enabled: authorized,
    networkMode: 'always',
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (query.data) setDraft(editable(query.data));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (body: EditableSettings) => reengagementApi.saveSettings(body),
    networkMode: 'always',
    onSuccess: (settings) => {
      setDraft(editable(settings));
      setSaved(true);
      queryClient.setQueryData(['reengagement', 'settings'], settings);
      void queryClient.invalidateQueries({ queryKey: ['reengagement', 'summary'] });
      void queryClient.invalidateQueries({ queryKey: ['reengagement', 'list'] });
    },
  });

  if (!authorized) return null;

  function set<K extends keyof EditableSettings>(key: K, value: EditableSettings[K]) {
    setSaved(false);
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  }

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="reengagement-settings-heading">
      <div className="work-section-heading">
        <h2 id="reengagement-settings-heading" className="text-lg font-semibold">
          {t('settings.reengagementHeading')}
        </h2>
      </div>
      <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('settings.reengagementDescription')}</p>
      <p className="mt-2 text-sm font-medium">{t('settings.reengagementHumanReview')}</p>

      {query.isLoading ? <LoadingBlock label={t('settings.reengagementLoading')} /> : null}

      {query.isError ? (
        <div className="mt-4">
          <Alert tone="error" title={t('settings.reengagementLoadError')}>
            <Button className="mt-2" onClick={() => void query.refetch()}>
              {t('reengagement.tryAgain')}
            </Button>
          </Alert>
        </div>
      ) : null}

      {draft ? (
        <form
          className="mt-5"
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate(draft);
          }}
        >
          <Checkbox
            label={t('settings.reengagementEnabled')}
            hint={t('settings.reengagementEnabledHint')}
            checked={draft.enabled}
            onChange={(event) => set('enabled', event.target.checked)}
          />

          <fieldset disabled={!draft.enabled} className="mt-5 border-t border-[color:var(--color-line)] pt-5">
            <legend className="mb-3 text-base font-semibold">{t('settings.reengagementRulesHeading')}</legend>
            <div className="grid gap-x-4 sm:grid-cols-2">
              <NumberInput
                label={t('settings.reengagementInactiveDays')}
                hint={t('settings.reengagementInactiveDaysHint')}
                min={30}
                max={730}
                value={draft.inactiveDays}
                onValueChange={(value) => set('inactiveDays', value ?? draft.inactiveDays)}
              />
              <NumberInput
                label={t('settings.reengagementCadenceDays')}
                hint={t('settings.reengagementCadenceDaysHint')}
                min={1}
                max={90}
                value={draft.reminderCadenceDays}
                onValueChange={(value) => set('reminderCadenceDays', value ?? draft.reminderCadenceDays)}
              />
              <NumberInput
                label={t('settings.reengagementCancellationCount')}
                hint={t('settings.reengagementCancellationCountHint')}
                min={2}
                max={10}
                value={draft.cancellationCount}
                onValueChange={(value) => set('cancellationCount', value ?? draft.cancellationCount)}
              />
              <NumberInput
                label={t('settings.reengagementCancellationWindow')}
                hint={t('settings.reengagementCancellationWindowHint')}
                min={7}
                max={365}
                value={draft.cancellationWindowDays}
                onValueChange={(value) => set('cancellationWindowDays', value ?? draft.cancellationWindowDays)}
              />
            </div>
          </fieldset>

          <fieldset disabled={!draft.enabled} className="mt-5 border-t border-[color:var(--color-line)] pt-5">
            <legend className="mb-2 text-base font-semibold">{t('settings.reengagementNotificationsHeading')}</legend>
            <p className="mb-4 text-sm text-[color:var(--color-muted)]">
              {t('settings.reengagementNotificationsHint')}
            </p>

            {query.data?.notificationsAvailable ? (
              <>
                <Checkbox
                  label={t('settings.reengagementNotificationsEnabled')}
                  hint={t('settings.reengagementNotificationsEnabledHint')}
                  checked={draft.notificationsEnabled}
                  onChange={(event) => set('notificationsEnabled', event.target.checked)}
                />
                <div className="grid gap-x-4 sm:grid-cols-2">
                  <Checkbox
                    label={t('settings.reengagementEmailNotifications')}
                    checked={draft.emailNotificationsEnabled}
                    disabled={!draft.notificationsEnabled}
                    onChange={(event) => set('emailNotificationsEnabled', event.target.checked)}
                  />
                  <Checkbox
                    label={t('settings.reengagementSmsNotifications')}
                    checked={draft.smsNotificationsEnabled}
                    disabled={!draft.notificationsEnabled}
                    onChange={(event) => set('smsNotificationsEnabled', event.target.checked)}
                  />
                </div>
              </>
            ) : (
              <Alert tone="info">{t('settings.reengagementNotificationsUnavailable')}</Alert>
            )}
          </fieldset>

          {mutation.isError ? (
            <div className="mt-4">
              <Alert tone="error">{t('settings.reengagementSaveError')}</Alert>
            </div>
          ) : null}
          {saved ? (
            <p className="mt-4 text-sm font-medium text-[color:var(--color-brand)]" role="status">
              {t('settings.reengagementSaved')}
            </p>
          ) : null}

          <div className="mt-5 flex justify-end border-t border-[color:var(--color-line)] pt-4">
            <Button type="submit" variant="primary" busy={mutation.isPending}>
              {t('settings.reengagementSave')}
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
